import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Alert, Spinner, Row, Col, ListGroup, Badge } from 'react-bootstrap';
import axios from 'axios';
import { FaFileUpload, FaCheck, FaExclamationTriangle, FaChartLine, FaSync, FaSyncAlt } from 'react-icons/fa';
import { useNotifications } from '../../context/NotificationContext';
import { submitToLender, getLenderMatchSettings } from '../../api/lenderApi';
import { getActiveLenders } from '../../api/lenderSettingsApi';
import LenderMatchScore from './LenderMatchScore';

// Custom styles for form labels
const labelStyle = {
  fontWeight: '600',
  color: '#495057',
  marginBottom: '0.25rem', // Reduced margin between label and input
  fontSize: '0.95rem'
};

/**
 * LenderSubmission Component
 * 
 * Handles the submission of contact and business data to various lenders.
 * Uses provided bank statements and application forms.
 * Shows submission status, results, and error messages.
 * Integrates with the notification system to display toast notifications.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.contactData - Contact information
 * @param {Object} props.opportunityData - Business opportunity data (renamed to businessData internally)
 * @param {Array} props.bankFiles - Array of bank statement files
 * @param {Array} props.appFiles - Array of application document files
 * @param {boolean} [props.disabled=false] - Whether submission is disabled
 * @param {Object} [props.submissionHistory={}] - History of previous submissions (lenderId: submissionDate)
 * @returns {JSX.Element} LenderSubmission component
 */
const LenderSubmission = ({ contactData, opportunityData, bankFiles = [], appFiles = [], disabled = false, submissionHistory = {} }) => {
  // Rename the prop internally for clarity
  const businessData = opportunityData;
  
  /**
   * Lenders list loaded from database
   * Each lender has an id (used for API calls) and a display name
   */
  const [lenders, setLenders] = useState([]);
  const [loadingLenders, setLoadingLenders] = useState(true);
  const [lendersError, setLendersError] = useState(null);
  
  // State for submission status
  const [submitting, setSubmitting] = useState(false);
  const [currentLender, setCurrentLender] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // NEW: State for tracking multiple submissions
  const [submittingLenders, setSubmittingLenders] = useState({});
  const [results, setResults] = useState({});
  const [errors, setErrors] = useState({});
  
  // State for lender match functionality
  const [matchEnabled, setMatchEnabled] = useState(false);
  const [matchCriteria, setMatchCriteria] = useState([]);
  const [matchScores, setMatchScores] = useState({});
  const [expandedLenders, setExpandedLenders] = useState({});
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [lastScoreUpdate, setLastScoreUpdate] = useState(null);
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  
  // Access notification context
  const { success, error: notifyError, info } = useNotifications();
  
  // List of field IDs that contain numeric values but are stored as text
  const numericTextFields = [
    'AverageBankBalance',
    'AnnualRevenue',
    'RequestedAmount',
    'ContactOwnershipPercentage',
    'FederalTaxID',
    // Add any other fields that should be treated as numbers
  ];
  
  // Helper function to determine if a field should be treated as numeric
  const isNumericField = (fieldId) => {
    return numericTextFields.includes(fieldId);
  };
  
  // Load lender match settings on component mount
  useEffect(() => {
    const loadMatchSettings = async () => {
      try {
        setLoadingMatch(true);
        const settings = await getLenderMatchSettings();
        
        setMatchEnabled(settings.enabled);
        
        if (settings.enabled && settings.lenders && settings.lenders.length > 0) {
          setMatchCriteria(settings.lenders);
          
          // If enabled, calculate scores
          calculateMatchScores(settings.lenders);
        }
      } catch (err) {
        console.error('Error loading lender match settings:', err);
      } finally {
        setLoadingMatch(false);
      }
    };
    
    loadMatchSettings();
  }, []);
  
  // Load active lenders from database on component mount
  useEffect(() => {
    const loadLenders = async () => {
      try {
        setLoadingLenders(true);
        setLendersError(null);
        
        console.log('Loading active lenders from database...');
        const activeLenders = await getActiveLenders();
        
        console.log(`Loaded ${activeLenders.length} active lenders:`, activeLenders.map(l => l.name));
        
        // If no lenders in database, use a minimal fallback
        if (activeLenders.length === 0) {
          console.warn('No active lenders found in database, using fallback lenders');
          setLenders([
            { id: 'kapitus', name: 'Kapitus', emailOnly: false },
            { id: 'credibly', name: 'Credibly', emailOnly: false }
          ]);
        } else {
          setLenders(activeLenders);
        }
        
      } catch (err) {
        console.error('Error loading active lenders:', err);
        setLendersError(err.toString());
        
        // Use fallback lenders if API fails
        console.warn('API failed, using fallback lenders');
        setLenders([
          { id: 'kapitus', name: 'Kapitus', emailOnly: false },
          { id: 'credibly', name: 'Credibly', emailOnly: false },
          { id: 'garden', name: 'Garden Funding', emailOnly: true }
        ]);
        
        // Only show error notification if it's not an authentication error
        if (!err.toString().includes('401') && !err.toString().includes('Unauthorized')) {
          notifyError(`Failed to load lenders: ${err}`, 'Loading Error');
        }
      } finally {
        setLoadingLenders(false);
      }
    };
    
    loadLenders();
  }, []); // Remove notifyError dependency to prevent infinite loop
  
  // Recalculate match scores whenever business data changes
  useEffect(() => {
    if (matchEnabled && matchCriteria.length > 0 && businessData) {
      console.log('Business data changed, recalculating match scores');
      // Use a small delay to ensure all form data is processed
      const timer = setTimeout(() => {
        calculateMatchScores(matchCriteria);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [businessData, contactData, matchEnabled, matchCriteria]);
  
  // Force recalculation when specific fields are updated through the form
  const forceRecalculateScores = () => {
    if (matchEnabled && matchCriteria.length > 0) {
      console.log('Forcing recalculation of match scores');
      calculateMatchScores(matchCriteria);
    }
  };
  
  // Get the most current field value, checking the DOM if necessary
  const getCurrentFieldValue = (fieldId) => {
    // First check businessData from props
    if (businessData && businessData[fieldId] !== undefined) {
      return businessData[fieldId];
    }
    
    // If not found or potentially stale, check for form input elements with matching ID or name
    try {
      // Look for input by field ID
      const inputElement = document.querySelector(`input[name="${fieldId}"], input[id="${fieldId}"]`);
      if (inputElement) {
        return inputElement.value;
      }
      
      // For select elements
      const selectElement = document.querySelector(`select[name="${fieldId}"], select[id="${fieldId}"]`);
      if (selectElement) {
        return selectElement.value;
      }
    } catch (err) {
      console.error('Error checking DOM for updated field value:', err);
    }
    
    // Fallback to businessData
    return businessData?.[fieldId];
  };
  
  // Evaluate each criterion with the most current data
  const evaluateCriterionWithCurrentData = (criterion) => {
    // Get the most up-to-date field value
    const fieldValue = getCurrentFieldValue(criterion.fieldId);
    return evaluateCriterion(criterion, fieldValue);
  };
  
  // Check if field is a date comparison field (BusinessStartDate or DateOfBirth)
  const isDateComparisonField = (fieldId) => {
    return fieldId === 'BusinessStartDate' || fieldId === 'DateOfBirth';
  };
  
  // Calculate the difference in months between two dates
  const getMonthDifference = (dateValue) => {
    if (!dateValue) return 0;
    
    try {
      // Parse the date from the YYYY-MM-DD format
      const [year, month, day] = String(dateValue).split('-').map(num => parseInt(num, 10));
      
      // Validate date components
      if (isNaN(year) || isNaN(month) || isNaN(day) || 
          year < 1900 || year > 2100 || 
          month < 1 || month > 12 || 
          day < 1 || day > 31) {
        console.error(`Invalid date format: ${dateValue}`);
        return 0;
      }
      
      // Create date objects
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed in JS
      const currentDate = new Date();
      
      // Additional validation: ensure the date object was created correctly
      if (dateObj.toString() === 'Invalid Date') {
        console.error(`Could not create valid date from: ${dateValue}`);
        return 0;
      }
      
      // Calculate difference in months
      const monthDiff = 
        (currentDate.getFullYear() - dateObj.getFullYear()) * 12 + 
        (currentDate.getMonth() - dateObj.getMonth());
      
      console.log(`Date difference calculation: 
        Date value: ${dateValue}
        Parsed date: ${dateObj.toISOString()}
        Current date: ${currentDate.toISOString()}
        Difference in months: ${monthDiff}`);
      
      return monthDiff;
    } catch (err) {
      console.error('Error calculating month difference:', err);
      return 0;
    }
  };
  
  // Calculate match scores based on criteria and business data
  const calculateMatchScores = (lenderCriteria) => {
    console.log('Recalculating match scores with current business data:', businessData);
    const scores = {};
    
    // Go through each lender's criteria
    lenderCriteria.forEach(lender => {
      console.log(`Evaluating criteria for lender: ${lender.name} (${lender.lenderId})`);
      
      // Skip if no criteria defined
      if (!lender.criteria || lender.criteria.length === 0) {
        scores[lender.lenderId] = {
          score: 0,
          matched: [],
          missed: [],
          disqualified: false
        };
        return;
      }
      
      let totalWeight = 0;
      let weightedScore = 0;
      const matched = [];
      const missed = [];
      let disqualified = false;
      
      // Evaluate each criterion
      lender.criteria.forEach(criterion => {
        // Find the most current field value
        const fieldValue = getCurrentFieldValue(criterion.fieldId);
        const weight = criterion.weight || 5;
        totalWeight += weight;
        
        // Check if the criterion is met
        const isMet = evaluateCriterion(criterion, fieldValue);
        
        if (isMet) {
          weightedScore += weight;
          matched.push(criterion);
        } else {
          missed.push(criterion);
          
          // If a required criterion is missed, the lender is disqualified
          if (criterion.required) {
            disqualified = true;
          }
        }
        
        // Log the evaluation result for debugging
        console.log(`Criterion ${criterion.fieldId} ${criterion.operator} ${criterion.value}:`, 
                    `Value: "${fieldValue}" (${typeof fieldValue})`, 
                    `CriterionValue: "${criterion.value}" (${typeof criterion.value})`,
                    `Met: ${isMet}`);
      });
      
      // Calculate the final score (0-100)
      let finalScore = totalWeight > 0 ? Math.round((weightedScore / totalWeight) * 100) : 0;
      
      // If disqualified by a required criterion, score is 0
      if (disqualified) {
        finalScore = 0;
      }
      
      // Store the score and details
      scores[lender.lenderId] = {
        score: finalScore,
        matched,
        missed,
        disqualified
      };
      
      console.log(`Final score for ${lender.name}: ${finalScore}%`);
    });
    
    // Update state with calculated scores
    setMatchScores(scores);
    
    // Sort lenders by score (highest first) — only when lenders are already loaded
    if (lenders.length > 0) {
      const sortedLenders = [...lenders].sort((a, b) => {
        const scoreA = scores[a.id]?.score || 0;
        const scoreB = scores[b.id]?.score || 0;
        return scoreB - scoreA;
      });
      setLenders(sortedLenders);
    }
    
    // Record update time and show update alert
    setLastScoreUpdate(new Date());
    setShowUpdateAlert(true);
    
    // Hide update alert after 3 seconds
    setTimeout(() => {
      setShowUpdateAlert(false);
    }, 3000);
  };
  
  // Normalize a value to a number if possible
  const normalizeNumericValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    
    // Remove any non-numeric characters except decimal point
    // First remove currency symbols, commas, etc.
    const cleanValue = String(value).replace(/[^0-9.-]/g, '');
    const parsedValue = parseFloat(cleanValue);
    
    return isNaN(parsedValue) ? 0 : parsedValue;
  };
  
  // Evaluate if a criterion is met by the field value
  const evaluateCriterion = (criterion, fieldValue) => {
    // If field value is undefined or null, criterion is not met
    if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
      console.log(`Field value is empty for ${criterion.fieldId}`);
      return false;
    }
    
    const { fieldId, operator, value } = criterion;
    
    // Special case for "older_than_months" operator on date fields
    if (isDateComparisonField(fieldId) && operator === 'older_than_months') {
      const monthsOld = getMonthDifference(fieldValue);
      const requiredMonths = parseInt(value, 10);
      
      console.log(`Date age comparison for ${fieldId}: ${monthsOld} months old vs required ${requiredMonths} months`);
      
      return monthsOld >= requiredMonths;
    }
    
    // Special handling for numeric text fields
    const isNumeric = isNumericField(fieldId) || 
                     (operator === '>' || operator === '>=' || operator === '<' || operator === '<=');
    
    if (isNumeric) {
      // Normalize both values for comparison
      const numFieldValue = normalizeNumericValue(fieldValue);
      const numCriterionValue = normalizeNumericValue(value);
      
      console.log(`Numeric comparison for ${fieldId}: ${numFieldValue} ${operator} ${numCriterionValue}`);
      
      // Compare as numbers
      switch (operator) {
        case '>':
          return numFieldValue > numCriterionValue;
        case '>=':
          return numFieldValue >= numCriterionValue;
        case '=':
          return numFieldValue === numCriterionValue;
        case '<=':
          return numFieldValue <= numCriterionValue;
        case '<':
          return numFieldValue < numCriterionValue;
        default:
          // For non-comparison operators on numeric fields
          if (operator === 'contains') {
            return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
          }
          return false;
      }
    }
    
    // Handle text operators for non-numeric fields
    switch (operator) {
      case '=':
        return String(fieldValue) === String(value);
      case 'contains':
        return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      default:
        return false;
    }
  };
  
  // NEW: Function to clear result/error for a specific lender
  const clearLenderStatus = (lenderId) => {
    setResults(prev => {
      const updated = { ...prev };
      delete updated[lenderId];
      return updated;
    });
    setErrors(prev => {
      const updated = { ...prev };
      delete updated[lenderId];
      return updated;
    });
  };
  
  /**
   * Submit contact and business data to a lender
   * 
   * @param {string} lenderId - ID of the lender to submit to
   */
  const handleSubmitToLender = async (lenderId) => {
    // Clear previous results and errors for this lender
    setResults(prev => ({ ...prev, [lenderId]: null }));
    setErrors(prev => ({ ...prev, [lenderId]: null }));
    
    // Get lender info
    const lender = lenders.find(l => l.id === lenderId);
    if (!lender) {
      console.error(`Lender with ID ${lenderId} not found`);
      return; // Prevent submission if lender info not found
    }
    
    // Mark this lender as submitting
    setSubmittingLenders(prev => ({ ...prev, [lenderId]: true }));
    
    // Show in-progress notification
    info(
      `Submitting to ${lender.name}...`,
      'Submission Started',
      3000 // Show for 3 seconds
    );
    
    try {
      // For backward compatibility - maintain single submission state if needed
      if (Object.keys(submittingLenders).length === 1) {
        setSubmitting(true);
        setCurrentLender(lender);
      }
      
      // Check if bank files are provided (required)
      if (!bankFiles || bankFiles.length === 0) {
        throw new Error('Bank statements are required for lender submission.');
      }
      
      // Submit to the lender API
      const response = await submitToLender(
        contactData,
        businessData,
        lenderId,
        bankFiles,
        appFiles
      );
      
      // Store the successful result for this lender
      setResults(prev => ({ ...prev, [lenderId]: response }));
      if (Object.keys(submittingLenders).length === 1) {
        setResult(response); // For backward compatibility
      }
      
      // Check for existing customer status and show appropriate notification
      if (response.existingCustomer || response.status === 'existing_customer') {
        // Show informational notification for existing customer
        info(
          response.message || `This application has already been submitted to ${lender.name}.`,
          `Existing Customer - ${lender.name}`,
          10000 // Show for 10 seconds
        );
      } else {
        // Show standard success notification
        success(
          `Successfully submitted to ${lender.name}`,
          'Success',
          8000 // Show for 8 seconds
        );
      }
    } catch (err) {
      console.error(`Error submitting to ${lender.name}:`, err);
      
      // Format error message using the error thrown by lenderApi
      const errorMessage = err.message || 'An error occurred during submission';
      
      // Store the error for this lender
      setErrors(prev => ({ ...prev, [lenderId]: errorMessage }));
      if (Object.keys(submittingLenders).length === 1) {
        setError(errorMessage); // For backward compatibility
      }
      
      // Show error notification using only the specific error message
      notifyError(
        errorMessage, // Use the specific message directly
        'Submission Failed', // Keep title simple
        10000 // Show longer for errors
      );
    } finally {
      // Mark this lender as no longer submitting
      setSubmittingLenders(prev => {
        const updated = { ...prev };
        delete updated[lenderId];
        
        // For backward compatibility - only set submitting to false if this was the last active submission
        if (Object.keys(updated).length === 0) {
          setSubmitting(false);
        }
        
        return updated;
      });
    }
  };
  
  // Toggle expanded state for a lender
  const toggleExpandLender = (lenderId) => {
    setExpandedLenders(prev => ({
      ...prev,
      [lenderId]: !prev[lenderId]
    }));
  };
  
  // Format date for display
  const formatSubmissionDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Format as MM/DD/YYYY
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    } catch (err) {
      console.error('Error formatting submission date:', err);
      return '';
    }
  };
  
  return (
    <Card className="shadow-sm border-0 mb-4">
      <Card.Body>
        
        {/* Match Score Section (when enabled) */}
        {matchEnabled && !loadingMatch && (
          <Card className="mt-4 mb-4 compact-lender-match">
            <Card.Header className="d-flex justify-content-between align-items-center py-2">
              <h5 className="mb-0 fs-6">Lender Match Results</h5>
              {lastScoreUpdate && (
                <span className="text-muted small fs-7">
                  Updated: {new Date(lastScoreUpdate).toLocaleString()}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-2 d-inline-flex align-items-center py-1 px-2"
                    onClick={forceRecalculateScores}
                    disabled={loadingMatch}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <FaSyncAlt size="0.8em" className={loadingMatch ? "spin-animation me-1" : "me-1"} />
                    {loadingMatch ? "Updating..." : "Refresh"}
                  </Button>
                </span>
              )}
            </Card.Header>
            <Card.Body className="p-2">
              <Row className="g-1">
                {lenders.map(lender => {
                  const lenderScore = matchScores[lender.id] || { 
                    score: 0, 
                    matched: [], 
                    missed: [] 
                  };
                  
                  return (
                    <Col md={6} lg={4} xl={3} key={lender.id} className="mb-2">
                      <LenderMatchScore
                        lenderId={lender.id}
                        lenderName={lender.name}
                        score={lenderScore.score}
                        matchedCriteria={lenderScore.matched}
                        missedCriteria={lenderScore.missed}
                        expanded={expandedLenders[lender.id] || false}
                        onToggleExpand={() => toggleExpandLender(lender.id)}
                      />
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>
        )}
        
        {/* Loading State */}
        {loadingLenders && (
          <div className="text-center py-4">
            <Spinner animation="border" className="me-2" />
            <span>Loading lenders...</span>
          </div>
        )}
        
        {/* Error State */}
        {lendersError && (
          <Alert variant="danger" className="mb-4">
            <strong>Error loading lenders:</strong> {lendersError}
            <Button 
              variant="outline-danger" 
              size="sm" 
              className="ms-2"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Alert>
        )}
        
        {/* Lender Buttons and Statuses */}
        {!loadingLenders && !lendersError && (
          <>
            <div className="mb-3">
              <h5 className="fs-6">Platform API Lenders</h5>
              <div className="lender-buttons-container d-flex flex-wrap justify-content-start gap-3 mt-2">
                {lenders.filter(lender => !lender.emailOnly).map(lender => {
              const isSubmitting = submittingLenders[lender.id];
              const lenderResult = results[lender.id];
              const lenderError = errors[lender.id];
              const previousSubmission = submissionHistory[lender.id];
              
              return (
                <div key={lender.id} className="lender-item d-flex flex-column align-items-center" style={{ width: '220px' }}>
                  <Button
                    variant="primary"
                    onClick={() => handleSubmitToLender(lender.id)}
                    disabled={disabled || submittingLenders[lender.id]}
                    className="d-flex align-items-center justify-content-center w-100 mb-2 lender-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <FaFileUpload className="me-2" />
                        <span>{lender.name}</span>
                      </>
                    )}
                  </Button>
                  
                  {/* Previous submission history display */}
                  {previousSubmission && !lenderResult && !lenderError && (
                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.8em' }}>
                      Last submitted: {formatSubmissionDate(previousSubmission)}
                    </small>
                  )}
                  
                  {/* Result display */}
                  {lenderResult && (
                    <Alert 
                      variant={lenderResult.existingCustomer || lenderResult.status === 'existing_customer' ? 'warning' : 'success'} 
                      className="w-100 p-2 mt-1 text-start small lender-status-alert"
                    >
                      {lenderResult.existingCustomer || lenderResult.status === 'existing_customer' ? (
                        <>
                          <FaExclamationTriangle className="me-1" /> 
                          <strong>Existing Customer: </strong>
                          {lenderResult.message || 'This application has already been submitted to this lender.'}
                        </>
                      ) : (
                        <>
                          <FaCheck className="me-1" /> 
                          Submission successful
                          {lenderResult.submissionId && (
                            <span className="ms-1">
                              (ID: <strong>{lenderResult.submissionId}</strong>)
                            </span>
                          )}
                        </>
                      )}
                    </Alert>
                  )}
                  
                  {/* Error display */}
                  {lenderError && (
                    <Alert 
                      variant="danger" 
                      className="w-100 p-2 mt-1 text-start small lender-status-alert" 
                      dismissible
                      onClose={() => clearLenderStatus(lender.id)}
                    >
                      <FaExclamationTriangle className="me-1" /> {lenderError}
                    </Alert>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
            <div>
              <h5 className="fs-6">Email Submission Lenders</h5>
              <div className="lender-buttons-container d-flex flex-wrap justify-content-start gap-3 mt-2">
                {lenders.filter(lender => lender.emailOnly).map(lender => {
              const isSubmitting = submittingLenders[lender.id];
              const lenderResult = results[lender.id];
              const lenderError = errors[lender.id];
              const previousSubmission = submissionHistory[lender.id];
              
              return (
                <div key={lender.id} className="lender-item d-flex flex-column align-items-center" style={{ width: '220px' }}>
                  <Button
                    variant="outline-primary"
                    onClick={() => handleSubmitToLender(lender.id)}
                    disabled={disabled || submittingLenders[lender.id]}
                    className="d-flex align-items-center justify-content-center w-100 mb-2 lender-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <FaFileUpload className="me-2" />
                        <span>{lender.name}</span>
                      </>
                    )}
                  </Button>
                  
                  {/* Previous submission history display */}
                  {previousSubmission && !lenderResult && !lenderError && (
                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.8em' }}>
                      Last submitted: {formatSubmissionDate(previousSubmission)}
                    </small>
                  )}
                  
                  {/* Result display */}
                  {lenderResult && (
                    <Alert 
                      variant={lenderResult.existingCustomer || lenderResult.status === 'existing_customer' ? 'warning' : 'success'} 
                      className="w-100 p-2 mt-1 text-start small lender-status-alert"
                    >
                      {lenderResult.existingCustomer || lenderResult.status === 'existing_customer' ? (
                        <>
                          <FaExclamationTriangle className="me-1" /> 
                          <strong>Existing Customer: </strong>
                          {lenderResult.message || 'This application has already been submitted to this lender.'}
                        </>
                      ) : (
                        <>
                          <FaCheck className="me-1" /> 
                          Submission successful
                          {lenderResult.submissionId && (
                            <span className="ms-1">
                              (ID: <strong>{lenderResult.submissionId}</strong>)
                            </span>
                          )}
                        </>
                      )}
                    </Alert>
                  )}
                  
                  {/* Error display */}
                  {lenderError && (
                    <Alert 
                      variant="danger" 
                      className="w-100 p-2 mt-1 text-start small lender-status-alert" 
                      dismissible
                      onClose={() => clearLenderStatus(lender.id)}
                    >
                      <FaExclamationTriangle className="me-1" /> {lenderError}
                    </Alert>
                  )}
                </div>
              );
                })}
              </div>
            </div>
          </>
        )}
        
        {/* Backward compatibility display */}
        {(result || error) && !Object.keys(results).length && !Object.keys(errors).length && (
          <Alert variant={error ? 'danger' : (result.existingCustomer || result.status === 'existing_customer' ? 'warning' : 'success')} className="mt-3">
            {error ? (
              <>
                <FaExclamationTriangle className="me-2" />
                {error}
              </>
            ) : result.existingCustomer || result.status === 'existing_customer' ? (
              <>
                <FaExclamationTriangle className="me-2" />
                <strong>Existing Customer: </strong>
                {result.message || `This application has already been submitted to ${currentLender?.name}.`}
              </>
            ) : (
              <>
                <FaCheck className="me-2" />
                Successfully submitted to {currentLender?.name}.
                {result.submissionId && (
                  <div className="mt-1">
                    Submission ID: <strong>{result.submissionId}</strong>
                  </div>
                )}
              </>
            )}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default LenderSubmission; 